import { useState, useEffect } from 'react';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadialBarChart, RadialBar, Legend } from 'recharts';
import { TrendingUp, TrendingDown, Minus, RefreshCw, Building2, Users, BarChart3, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function CompareCard({ label, mine, market, unit = '%', higherIsBetter = false }) {
  const diff = mine - market;
  const isGood = higherIsBetter ? diff >= 0 : diff <= 0;
  const isNeutral = Math.abs(diff) < 0.5;
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-sm text-gray-500 mb-3">{label}</p>
        <div className="flex items-end justify-between">
          <div>
            <p className="text-3xl font-black text-gray-900">{mine}{unit}</p>
            <p className="text-xs text-gray-400 mt-1">Tu restaurante</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-gray-500">{market}{unit}</p>
            <p className="text-xs text-gray-400">Promedio mercado</p>
          </div>
        </div>
        <div className={`mt-3 flex items-center gap-1.5 text-sm font-semibold ${isNeutral ? 'text-gray-500' : isGood ? 'text-emerald-600' : 'text-red-500'}`}>
          {isNeutral ? <Minus className="w-4 h-4" /> : isGood ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          {isNeutral ? 'En línea con el mercado' : isGood ? `${Math.abs(diff).toFixed(1)}${unit} mejor que el mercado` : `${Math.abs(diff).toFixed(1)}${unit} por debajo del mercado`}
        </div>
        <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${isNeutral ? 'bg-gray-400' : isGood ? 'bg-emerald-500' : 'bg-red-500'}`} style={{ width: `${Math.min(100, (mine / (market * 1.5)) * 100)}%` }} />
        </div>
      </CardContent>
    </Card>
  );
}

export default function MarketBenchmark() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/benchmark/market`, { headers: authHeader() });
      if (res.ok) setData(await res.json());
      else toast.error('Error al cargar benchmark');
    } catch { toast.error('Error de conexión'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const chartData = data ? [
    { name: 'No-shows', mine: data.mine.noshow_rate, market: data.market.noshow_rate },
    { name: 'Cancelaciones', mine: data.mine.cancel_rate, market: data.market.cancel_rate },
    { name: 'Tamaño grupo', mine: data.mine.avg_party_size, market: data.market.avg_party_size },
  ] : [];

  const insights = data ? [
    data.mine.noshow_rate < data.market.noshow_rate
      ? { type: 'success', text: `Tu tasa de no-shows (${data.mine.noshow_rate}%) es mejor que el mercado (${data.market.noshow_rate}%). ¡Sigue así!` }
      : { type: 'warning', text: `Tu tasa de no-shows (${data.mine.noshow_rate}%) está por encima del mercado (${data.market.noshow_rate}%). Considera activar depósitos o recordatorios automáticos.` },
    data.mine.cancel_rate < data.market.cancel_rate
      ? { type: 'success', text: `Tus cancelaciones (${data.mine.cancel_rate}%) están por debajo del promedio (${data.market.cancel_rate}%).` }
      : { type: 'warning', text: `Tus cancelaciones (${data.mine.cancel_rate}%) superan el promedio (${data.market.cancel_rate}%). Activa la política de cancelación anticipada.` },
    data.mine.avg_party_size >= data.market.avg_party_size
      ? { type: 'success', text: `Tu tamaño promedio de grupo (${data.mine.avg_party_size} personas) está por encima del mercado. Esto incrementa tu RevPASH.` }
      : { type: 'info', text: `Tu tamaño promedio de grupo (${data.mine.avg_party_size} personas) es menor al mercado (${data.market.avg_party_size}). Considera promociones para grupos.` },
  ] : [];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <TopBar title="Benchmark de Mercado" subtitle="Compara tu desempeño con el promedio anónimo de todos los restaurantes en OkReserva" helpSection="benchmark" />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <RefreshCw className="w-10 h-10 text-orange-500 animate-spin" />
            <p className="text-gray-500">Calculando benchmarks...</p>
          </div>
        ) : !data ? (
          <div className="text-center py-16 text-gray-400">
            <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No hay datos suficientes todavía</p>
          </div>
        ) : (
          <>
            {/* Network Banner */}
            <div className="bg-black text-white rounded-2xl p-6 flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-orange-400 text-sm font-semibold uppercase tracking-wider mb-1">Red OkReserva</p>
                <h2 className="text-2xl font-black">Datos de {data.market.total_restaurants} restaurantes</h2>
                <p className="text-gray-400 mt-1">Benchmarks calculados con {data.market.total_reservations.toLocaleString()} reservaciones — datos 100% anónimos</p>
              </div>
              <Button onClick={load} variant="outline" className="border-white/20 text-white hover:bg-white/10 gap-2">
                <RefreshCw className="w-4 h-4" /> Actualizar
              </Button>
            </div>

            {/* Comparison Cards */}
            <div className="grid md:grid-cols-3 gap-4">
              <CompareCard label="Tasa de No-Shows" mine={data.mine.noshow_rate} market={data.market.noshow_rate} />
              <CompareCard label="Tasa de Cancelaciones" mine={data.mine.cancel_rate} market={data.market.cancel_rate} />
              <CompareCard label="Tamaño Promedio de Grupo" mine={data.mine.avg_party_size} market={data.market.avg_party_size} unit=" pers" higherIsBetter />
            </div>

            {/* Chart */}
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-orange-500" /> Tu restaurante vs. Mercado</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="mine" name="Tu restaurante" fill="#f97316" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="market" name="Mercado" fill="#e5e7eb" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Insights */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Recomendaciones personalizadas</h3>
              <div className="space-y-3">
                {insights.map((ins, i) => (
                  <div key={i} className={`rounded-xl p-4 border flex gap-3 ${
                    ins.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
                    ins.type === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-800' :
                    'bg-blue-50 border-blue-200 text-blue-800'
                  }`}>
                    {ins.type === 'warning' ? <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <TrendingUp className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                    <p className="text-sm">{ins.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer Note */}
            <p className="text-xs text-gray-400 text-center pb-4">
              Los datos se calculan de forma agregada y completamente anónima. Ningún restaurante puede identificar la información de otro establecimiento.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
